import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";

import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";

import {
  toast,
  ToastContainer,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import {
  FaPlay,
  FaArrowRight,
  FaArrowLeft,
  FaEye,
  FaTimesCircle,
  FaPrint,
  FaRedo,
  FaDatabase,
  FaCheck,
  FaExclamationTriangle,
} from "react-icons/fa";


const FinYearDataBaseChange = () => {

  const BASE_URL = "https://nissi-india.com";


  // ============================================================
  // THEME
  // ============================================================

  const {
    isDark,
    skin,
    setSkin,
  } = useContext(ThemeContext);

  const currentSkin =
    getCurrentSkin(skin);

  const {
    baseColor,
    baseShade,
  } = currentSkin;


  const buttonClass =
    `bg-${baseColor}-${baseShade}`;

  const hoverClass =
    `hover:bg-${baseColor}-700`;


  // ============================================================
  // PANELS
  // ============================================================

  /*
    0 = Terms
    1 = Pending
    2 = Backup / Restore
    3 = Check
    4 = Clear
    5 = Opening Stock
    6 = Finalize
  */

  const [panel, setPanel] =
    useState(0);


  // Pending module detail view

  const [
    pendingModuleView,
    setPendingModuleView,
  ] = useState(null);


  // ============================================================
  // COMMON STATE
  // ============================================================

  const [loading, setLoading] =
    useState(false);

  const [info, setInfo] =
    useState(null);


  const finYear =
    info?.nextFY || "";


  // ============================================================
  // PENDING STATE
  // ============================================================

  const [
    pendingSummary,
    setPendingSummary,
  ] = useState(null);

  const [
    pendingList,
    setPendingList,
  ] = useState([]);

  const [
    pendingListLoading,
    setPendingListLoading,
  ] = useState(false);

  const [
    selectedIds,
    setSelectedIds,
  ] = useState({});

  const [
    branchId,
    setBranchId,
  ] = useState("%");

  const [
    branches,
    setBranches,
  ] = useState([
    {
      id: "%",
      name: "All Branches",
    },
  ]);


  // ============================================================
  // BACKUP STATUS
  // ============================================================

  const [
    backupStatus,
    setBackupStatus,
  ] = useState({

    Inventory: {
      done: false,
      curDb: "",
      newDb: "",
    },

    Tally: {
      done: false,
      curDb: "",
      newDb: "",
    },

  });


  // ============================================================
  // CHECK STATUS
  // ============================================================

  const [
    checkStatus,
    setCheckStatus,
  ] = useState({

    Inventory: {
      done: false,
      results: [],
    },

    Tally: {
      done: false,
      results: [],
    },

  });


  // ============================================================
  // CLEAR STATUS
  // ============================================================

  const [
    clearStatus,
    setClearStatus,
  ] = useState({

    Inventory: {
      done: false,
      cleared: 0,
      tables: [],
    },

    Tally: {
      done: false,
      cleared: 0,
      tables: [],
    },

  });


  // ============================================================
  // OPENING STOCK STATUS
  // ============================================================

  const [
    openingStatus,
    setOpeningStatus,
  ] = useState({

    done: false,

    status: null,

    counts: null,

  });


  // ============================================================
  // FINAL STATUS
  // ============================================================

  const [
    finalStatus,
    setFinalStatus,
  ] = useState({

    done: false,

    fromFinYear: "",

    toFinYear: "",

    newInvDb: "",

    newTallyDb: "",

  });


  // ============================================================
  // USER
  // ============================================================

  const userId = useMemo(
    () =>
      Number(
        localStorage.getItem("userId") ||
          0
      ),
    []
  );

  const ip = "";


  // ============================================================
  // HELPERS
  // ============================================================

  const niceModuleName = (m) => {

    const map = {

      PR: "Purchase Request",

      QTN: "Quotation",

      PO: "Purchase Order",

      GRN: "GRN",

      BP: "Bill Pass",

      TALLY: "Tally Integration",

    };

    return map[m] || m;
  };


  const canGoNextFromPending =
    useMemo(() => {

      return !!pendingSummary?.allowProceed;

    }, [pendingSummary]);


  const step2Ready =
    backupStatus.Inventory.done &&
    backupStatus.Tally.done;


  const step3Ready =
    checkStatus.Inventory.done &&
    checkStatus.Tally.done;


  const step4Ready =
    clearStatus.Inventory.done &&
    clearStatus.Tally.done;


  const step5Ready =
    openingStatus.done;


  const step6Ready =
    finalStatus.done;


  const apiErrorToast = (
    err,
    fallback = "Server error"
  ) => {

    toast.error(
      err?.response?.data?.message ||
        err?.message ||
        fallback
    );

  };


  const handlePrint = () => {
    window.print();
  };


  // ============================================================
  // LOAD INFO
  // ============================================================

  const loadInfo = async () => {

    const res =
      await axios.get(
        `${BASE_URL}/api/finyear-change/info`
      );

    if (!res.data?.ok) {

      throw new Error(
        res.data?.message ||
          "Failed to load info"
      );

    }

    setInfo(res.data);

  };


  // ============================================================
  // LOAD PENDING SUMMARY
  // ============================================================

  const loadPendingSummary =
    async (fy) => {

      const res =
        await axios.get(
          `${BASE_URL}/api/finyear-change/pending/summary`,
          {
            params: {
              finYear: fy,
            },
          }
        );

      if (!res.data?.ok) {

        throw new Error(
          res.data?.message ||
            "Failed to load pending summary"
        );

      }

      setPendingSummary(
        res.data
      );

    };


  // ============================================================
  // LOAD BRANCHES
  // ============================================================

  const loadBranchesOptional =
    async () => {

      try {

        const res =
          await axios.get(
            `${BASE_URL}/api/branches/list`
          );

        const list =
          res.data?.list ||
          res.data?.rows ||
          [];


        if (
          Array.isArray(list) &&
          list.length
        ) {

          const mapped =
            list.map((b) => ({

              id:
                b.fld_Branchid ??
                b.id,

              name:
                b.fld_BranchName ??
                b.name ??
                "",

            }));


          setBranches([

            {
              id: "%",
              name: "All Branches",
            },

            ...mapped,

          ]);

        }

      } catch {

        // optional API
      }

    };


  // ============================================================
  // INITIAL LOAD
  // ============================================================

  useEffect(() => {

    (async () => {

      try {

        setLoading(true);

        await loadInfo();

        await loadBranchesOptional();

      } catch (e) {

        apiErrorToast(
          e,
          "Failed to load Financial Year info"
        );

      } finally {

        setLoading(false);

      }

    })();

  }, []);


  useEffect(() => {

    if (!finYear) {
      return;
    }

    loadPendingSummary(
      finYear
    ).catch((e) => {

      apiErrorToast(
        e,
        "Failed to load pending summary"
      );

    });

  }, [finYear]);


  // ============================================================
  // PENDING LIST
  // ============================================================

  const fetchPendingList =
    async (module) => {

      try {

        setPendingListLoading(true);

        setSelectedIds({});


        const res =
          await axios.get(
            `${BASE_URL}/api/finyear-change/pending/list`,
            {
              params: {

                finYear,

                module,

                branchId,

              },
            }
          );


        if (!res.data?.ok) {

          throw new Error(
            res.data?.message ||
              "Failed to load list"
          );

        }


        setPendingList(
          res.data.rows || []
        );

        setPendingModuleView(
          module
        );

      } catch (e) {

        apiErrorToast(
          e,
          "Failed to load pending list"
        );

      } finally {

        setPendingListLoading(
          false
        );

      }

    };


  // ============================================================
  // SELECT ALL
  // ============================================================

  const toggleSelectAll =
    (checked) => {

      const next = {};

      if (checked) {

        for (
          const row of pendingList
        ) {

          const rid =
            row.fld_id ??
            row.id ??
            row.fld_refid;

          if (rid != null) {

            next[
              String(rid)
            ] = true;

          }

        }

      }

      setSelectedIds(next);

    };


  // ============================================================
  // SELECT ROW
  // ============================================================

  const toggleRow = (
    rowId,
    checked
  ) => {

    setSelectedIds(
      (prev) => {

        const next = {
          ...prev,
        };

        if (checked) {

          next[
            String(rowId)
          ] = true;

        } else {

          delete next[
            String(rowId)
          ];

        }

        return next;

      }
    );

  };


  // ============================================================
  // CANCEL PENDING
  // ============================================================

  const cancelSelected =
    async () => {

      const ids =
        Object.keys(
          selectedIds
        )
          .map((x) =>
            Number(x)
          )
          .filter(
            (n) =>
              !Number.isNaN(n)
          );


      if (!ids.length) {

        toast.error(
          "Please select records to cancel"
        );

        return;

      }


      const ok =
        window.confirm(
          `Cancel ${ids.length} selected record(s) in ${niceModuleName(
            pendingModuleView
          )}?`
        );


      if (!ok) {
        return;
      }


      if (!userId) {

        toast.error(
          "UserId missing. Please login again."
        );

        return;

      }


      try {

        setLoading(true);


        const res =
          await axios.post(
            `${BASE_URL}/api/finyear-change/pending/cancel`,
            {

              finYear,

              module:
                pendingModuleView,

              ids,

              userId,

              ip,

            }
          );


        if (!res.data?.ok) {

          throw new Error(
            res.data?.message ||
              "Cancel failed"
          );

        }


        toast.success(
          "Cancelled successfully"
        );


        await fetchPendingList(
          pendingModuleView
        );


        await loadPendingSummary(
          finYear
        );


      } catch (e) {

        apiErrorToast(
          e,
          "Cancel failed"
        );

      } finally {

        setLoading(false);

      }

    };


  // ============================================================
  // STEP 2 - BACKUP & RESTORE
  // ============================================================

  const doBackupRestore =
    async (dbType) => {

      const ok =
        window.confirm(
          `Proceed with ${dbType} Backup & Restore for ${finYear}?`
        );


      if (!ok) {
        return;
      }


      try {

        setLoading(true);


        const res =
          await axios.post(
            `${BASE_URL}/api/finyear-change/db/backup-restore`,
            {

              finYear,

              dbType,

            }
          );


        if (!res.data?.ok) {

          throw new Error(
            res.data?.message ||
              "Backup/Restore failed"
          );

        }


        setBackupStatus(
          (prev) => ({

            ...prev,

            [dbType]: {

              done: true,

              curDb:
                res.data.curDb,

              newDb:
                res.data.newDb,

            },

          })
        );


        toast.success(
          `${dbType} Backup & Restore completed`
        );


      } catch (e) {

        apiErrorToast(
          e,
          `${dbType} Backup/Restore failed`
        );

      } finally {

        setLoading(false);

      }

    };


  // ============================================================
  // STEP 3 - CHECK
  // ============================================================

  const doCheck =
    async (dbType) => {

      try {

        setLoading(true);


        const res =
          await axios.post(
            `${BASE_URL}/api/finyear-change/db/check`,
            {

              finYear,

              dbType,

            }
          );


        if (!res.data?.ok) {

          throw new Error(
            res.data?.message ||
              "Check failed"
          );

        }


        setCheckStatus(
          (prev) => ({

            ...prev,

            [dbType]: {

              done: true,

              results:
                res.data.results ||
                [],

            },

          })
        );


        toast.success(
          `${dbType} check completed`
        );


      } catch (e) {

        apiErrorToast(
          e,
          `${dbType} check failed`
        );

      } finally {

        setLoading(false);

      }

    };


  // ============================================================
  // STEP 4 - CLEAR
  // ============================================================

  const doClear =
    async (dbType) => {

      const ok =
        window.confirm(
          `Clear NEW ${dbType} DB for ${finYear}?\n\nThis will delete transactional data from the NEW database.`
        );


      if (!ok) {
        return;
      }


      try {

        setLoading(true);


        const res =
          await axios.post(
            `${BASE_URL}/api/finyear-change/db/clear`,
            {

              finYear,

              dbType,

            }
          );


        if (!res.data?.ok) {

          throw new Error(
            res.data?.message ||
              "Clear failed"
          );

        }


        setClearStatus(
          (prev) => ({

            ...prev,

            [dbType]: {

              done: true,

              cleared:
                res.data.cleared ||
                0,

              tables:
                res.data.tables ||
                [],

            },

          })
        );


        toast.success(
          `${dbType} cleared (${res.data.cleared || 0} tables)`
        );


      } catch (e) {

        apiErrorToast(
          e,
          `${dbType} clear failed`
        );

      } finally {

        setLoading(false);

      }

    };


  // ============================================================
  // RESET CLEAR
  // ============================================================

  const resetClear =
    async (dbType) => {

      const ok =
        window.confirm(
          `Reset clear status for ${dbType}?`
        );


      if (!ok) {
        return;
      }


      try {

        setLoading(true);


        const res =
          await axios.post(
            `${BASE_URL}/api/finyear-change/db/clear/reset`,
            {

              dbType,

            }
          );


        if (!res.data?.ok) {

          throw new Error(
            res.data?.message ||
              "Reset failed"
          );

        }


        setClearStatus(
          (prev) => ({

            ...prev,

            [dbType]: {

              done: false,

              cleared: 0,

              tables: [],

            },

          })
        );


        toast.success(
          `${dbType} clear reset done`
        );


      } catch (e) {

        apiErrorToast(
          e,
          "Reset failed"
        );

      } finally {

        setLoading(false);

      }

    };


  // ============================================================
  // STEP 5 - OPENING STOCK
  // ============================================================

  const doOpening =
    async () => {

      const ok =
        window.confirm(
          `Add Opening Stock in NEW Inventory DB for ${finYear}?`
        );


      if (!ok) {
        return;
      }


      try {

        setLoading(true);


        const res =
          await axios.post(
            `${BASE_URL}/api/finyear-change/db/opening-stock`,
            {

              finYear,

            }
          );


        if (!res.data?.ok) {

          throw new Error(
            res.data?.message ||
              "Opening stock failed"
          );

        }


        setOpeningStatus({

          done: true,

          status:
            res.data.status,

          counts:
            res.data.counts,

        });


        toast.success(
          "Opening stock added"
        );


      } catch (e) {

        apiErrorToast(
          e,
          "Opening stock failed"
        );

      } finally {

        setLoading(false);

      }

    };


  // ============================================================
  // RESET OPENING
  // ============================================================

  const resetOpening =
    async () => {

      const ok =
        window.confirm(
          `Reset Opening Stock step for ${finYear}?\n\nThis deletes branch_items, ledger and fifo data in the NEW database.`
        );


      if (!ok) {
        return;
      }


      try {

        setLoading(true);


        const res =
          await axios.post(
            `${BASE_URL}/api/finyear-change/db/opening-stock/reset`,
            {

              finYear,

            }
          );


        if (!res.data?.ok) {

          throw new Error(
            res.data?.message ||
              "Reset opening failed"
          );

        }


        setOpeningStatus({

          done: false,

          status: null,

          counts: null,

        });


        toast.success(
          "Opening stock reset done"
        );


      } catch (e) {

        apiErrorToast(
          e,
          "Reset opening failed"
        );

      } finally {

        setLoading(false);

      }

    };


  // ============================================================
  // STEP 6 - FINALIZE
  // ============================================================

  const doFinalize =
    async () => {

      const ok =
        window.confirm(
          `FINALIZE Financial Year change to ${finYear}?\n\nThis will switch the ACTIVE Inventory and Tally database connections.\n\nProceed carefully.`
        );


      if (!ok) {
        return;
      }


      try {

        setLoading(true);


        const res =
          await axios.post(
            `${BASE_URL}/api/finyear-change/finalize`,
            {

              finYear,

            }
          );


        if (!res.data?.ok) {

          throw new Error(
            res.data?.message ||
              "Finalize failed"
          );

        }


        setFinalStatus({

          done: true,

          fromFinYear:
            res.data.fromFinYear,

          toFinYear:
            res.data.toFinYear,

          newInvDb:
            res.data.newInvDb,

          newTallyDb:
            res.data.newTallyDb,

        });


        toast.success(
          "Financial year changed successfully"
        );


      } catch (e) {

        apiErrorToast(
          e,
          "Finalize failed"
        );

      } finally {

        setLoading(false);

      }

    };


  // ============================================================
  // UI COMPONENTS
  // ============================================================

  const Card = ({
    title,
    children,
    right,
  }) => (

    <div
      className={`
        rounded-2xl
        shadow-xl
        border
        p-6
        mb-6
        ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : "bg-white text-black border-gray-200"
        }
      `}
    >

      <div
        className="
          flex
          justify-between
          items-center
          mb-4
          gap-3
        "
      >

        <h2
          className={`
            text-xl
            font-bold
            ${
              isDark
                ? "text-white"
                : "text-gray-900"
            }
          `}
        >
          {title}
        </h2>

        {right}

      </div>

      {children}

    </div>

  );


  const PrimaryBtn = ({
    children,
    onClick,
    disabled,
  }) => (

    <button
      type="button"
      onClick={onClick}
      disabled={
        disabled ||
        loading
      }
      className={`
        inline-flex
        items-center
        gap-2
        px-5
        py-2
        rounded-lg
        font-semibold
        shadow-lg
        hover:shadow-xl
        transform
        hover:-translate-y-0.5
        transition-all
        duration-200
        ${buttonClass}
        ${hoverClass}
        text-white
        disabled:opacity-60
        disabled:cursor-not-allowed
      `}
    >
      {children}
    </button>

  );


  const SecondaryBtn = ({
    children,
    onClick,
    disabled,
  }) => (

    <button
      type="button"
      onClick={onClick}
      disabled={
        disabled ||
        loading
      }
      className={`
        inline-flex
        items-center
        gap-2
        px-5
        py-2
        rounded-lg
        font-semibold
        shadow-lg
        hover:shadow-xl
        transition-all
        duration-200
        ${
          isDark
            ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
            : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
        }
        disabled:opacity-60
        disabled:cursor-not-allowed
      `}
    >
      {children}
    </button>

  );


  const DangerBtn = ({
    children,
    onClick,
    disabled,
  }) => (

    <button
      type="button"
      onClick={onClick}
      disabled={
        disabled ||
        loading
      }
      className={`
        inline-flex
        items-center
        gap-2
        px-5
        py-2
        rounded-lg
        font-semibold
        shadow-lg
        transition-all
        ${
          isDark
            ? "bg-red-700 hover:bg-red-600 text-white"
            : "bg-red-600 hover:bg-red-700 text-white"
        }
        disabled:opacity-60
        disabled:cursor-not-allowed
      `}
    >
      {children}
    </button>

  );


  const StepNav = ({
    backDisabled,
    nextDisabled,
    onBack,
    onNext,
  }) => (

    <div
      className="
        flex
        justify-between
        mt-6
      "
    >

      <SecondaryBtn
        onClick={onBack}
        disabled={backDisabled}
      >
        <FaArrowLeft />
        Back
      </SecondaryBtn>


      <PrimaryBtn
        onClick={onNext}
        disabled={nextDisabled}
      >
        Next
        <FaArrowRight />
      </PrimaryBtn>

    </div>

  );


  const Badge = ({
    text,
    kind,
  }) => {

    let cls = "";


    if (kind === "ok") {

      cls =
        isDark
          ? "bg-green-900 text-green-100"
          : "bg-green-100 text-green-800";

    } else if (
      kind === "warn"
    ) {

      cls =
        isDark
          ? "bg-yellow-900 text-yellow-100"
          : "bg-yellow-100 text-yellow-800";

    } else {

      cls =
        isDark
          ? "bg-red-900 text-red-100"
          : "bg-red-100 text-red-800";

    }


    return (

      <span
        className={`
          px-3
          py-1
          rounded-full
          text-sm
          font-medium
          whitespace-nowrap
          ${cls}
        `}
      >
        {text}
      </span>

    );

  };


  // ============================================================
  // LOADING INFO
  // ============================================================

  if (!info) {

    return (

      <AdminLayoutWrapper>

        <div
          className={`
            min-h-screen
            ${
              isDark
                ? "bg-gray-900"
                : "bg-gray-100"
            }
          `}
        >

          <HeaderBar

            title={
              <span
                className="
                  font-dmserif
                  text-3xl
                "
              >
                Financial Year Database Change
              </span>
            }

            setSkin={setSkin}

            currentSkin={skin}

          />


          <div className="p-6">

            {
              loading
                ? "Loading..."
                : "Unable to load info"
            }

          </div>

        </div>

      </AdminLayoutWrapper>

    );

  }


  // ============================================================
  // MAIN RENDER
  // ============================================================

  return (

    <AdminLayoutWrapper>

      <div
        className={`
          min-h-screen
          ${
            isDark
              ? "bg-gray-900"
              : "bg-gray-100"
          }
        `}
      >

        <HeaderBar

          title={
            <span
              className="
                font-dmserif
                text-3xl
              "
            >
              Financial Year Database Change
            </span>
          }

          setSkin={setSkin}

          currentSkin={skin}

        />


        <div
          className="
            p-6
            mt-6
          "
        >


          {/* ================================================= */}
          {/* FIN YEAR INFO */}
          {/* ================================================= */}

          <div
            className={`
              p-4
              mb-6
              rounded-xl
              border
              ${
                isDark
                  ? "bg-gray-800 border-gray-700 text-white"
                  : "bg-white border-gray-200 text-gray-900"
              }
            `}
          >

            <div
              className="
                grid
                grid-cols-12
                gap-4
                items-center
              "
            >


              <div
                className="
                  col-span-12
                  md:col-span-6
                "
              >

                <div
                  className="
                    text-sm
                    opacity-80
                  "
                >
                  Active Financial Year
                </div>

                <div
                  className="
                    text-lg
                    font-bold
                  "
                >
                  {info.activeFY}
                </div>

                <div
                  className="
                    text-xs
                    opacity-70
                  "
                >
                  Inventory DB: {info.curInvDb}
                </div>

                <div
                  className="
                    text-xs
                    opacity-70
                  "
                >
                  Tally DB: {info.curTallyDb}
                </div>

              </div>


              <div
                className="
                  col-span-12
                  md:col-span-6
                "
              >

                <div
                  className="
                    text-sm
                    opacity-80
                  "
                >
                  New Financial Year
                </div>

                <div
                  className="
                    text-lg
                    font-bold
                  "
                >
                  {info.nextFY}
                </div>

                <div
                  className="
                    text-xs
                    opacity-70
                  "
                >
                  New Inventory DB: {info.newInvDb}
                </div>

                <div
                  className="
                    text-xs
                    opacity-70
                  "
                >
                  New Tally DB: {info.newTallyDb}
                </div>

              </div>

            </div>

          </div>


          {/* ================================================= */}
          {/* PANEL 0 */}
          {/* ================================================= */}

          {
            panel === 0 && (

              <Card

                title="IMPORTANT NOTES - Financial Year Setup"

                right={
                  <Badge
                    text="Step 0/6"
                    kind="warn"
                  />
                }

              >

                <div
                  className="
                    space-y-4
                    text-sm
                    leading-6
                  "
                >

                  <div
                    className="
                      font-semibold
                      text-base
                    "
                  >
                    STEP 1 : View all pending transactions as you start the process.
                  </div>


                  <div>

                    The{" "}

                    <span className="font-semibold">
                      NEW FINANCIAL YEAR SETUP ENGINE (NFYSE)
                    </span>

                    {" "}will display all entries that are still pending before you move to the new financial year.

                  </div>


                  <ul
                    className="
                      list-disc
                      pl-6
                      space-y-1
                    "
                  >

                    <li>
                      View all pending transactions from the current financial year.
                    </li>

                    <li>
                      Complete or cancel pending transactions where required.
                    </li>

                  </ul>


                  <div
                    className={`
                      p-3
                      rounded-lg
                      border
                      ${
                        isDark
                          ? "border-yellow-700 bg-yellow-900/30"
                          : "border-yellow-200 bg-yellow-50"
                      }
                    `}
                  >

                    <span className="font-semibold">
                      Note:
                    </span>

                    {" "}Verify all pending transactions before continuing to database creation.

                  </div>


                  <div className="space-y-2">

                    <div>
                      <strong>STEP 2 :</strong>{" "}
                      Create new databases using backup and restore.
                    </div>

                    <div>
                      <strong>STEP 3 :</strong>{" "}
                      Compare the old and new databases.
                    </div>

                    <div>
                      <strong>STEP 4 :</strong>{" "}
                      Clear transactional data from the new databases.
                    </div>

                    <div>
                      <strong>STEP 5 :</strong>{" "}
                      Add opening stock into the new Inventory database.
                    </div>

                    <div>
                      <strong>STEP 6 :</strong>{" "}
                      Change the active Financial Year database connections.
                    </div>

                  </div>


                  <div
                    className={`
                      p-3
                      rounded-lg
                      border
                      ${
                        isDark
                          ? "border-yellow-700 bg-yellow-900/30"
                          : "border-yellow-200 bg-yellow-50"
                      }
                    `}
                  >

                    <div
                      className="
                        font-semibold
                        flex
                        items-center
                        gap-2
                      "
                    >
                      <FaExclamationTriangle />
                      Warning
                    </div>

                    <div className="mt-1">

                      Finalize changes live database connections.

                      Proceed only after successful validation.

                    </div>

                  </div>


                  <div
                    className="
                      pt-4
                      flex
                      justify-end
                    "
                  >

                    <PrimaryBtn

                      onClick={() => {

                        setPanel(1);

                        toast.info(
                          "Proceeding to Pending Transactions..."
                        );

                      }}

                    >

                      <FaPlay />
                      Proceed

                    </PrimaryBtn>

                  </div>

                </div>

              </Card>

            )
          }


          {/* ================================================= */}
          {/* PANEL 1 - PENDING */}
          {/* ================================================= */}

          {
            panel === 1 && (

              <Card

                title="Step-1: Check Pending Transactions"

                right={
                  <Badge
                    text="Step 1/6"
                    kind="warn"
                  />
                }

              >

                {
                  pendingModuleView ? (

                    <div>

                      <div
                        className="
                          flex
                          flex-wrap
                          items-center
                          justify-between
                          gap-3
                          mb-4
                        "
                      >

                        <div
                          className="
                            text-lg
                            font-semibold
                          "
                        >
                          Pending List:{" "}

                          <span className="underline">
                            {
                              niceModuleName(
                                pendingModuleView
                              )
                            }
                          </span>

                        </div>


                        <div
                          className="
                            flex
                            flex-wrap
                            items-center
                            gap-2
                          "
                        >

                          <span
                            className="
                              text-sm
                              opacity-80
                            "
                          >
                            Branch:
                          </span>


                          <select

                            value={branchId}

                            onChange={(e) =>
                              setBranchId(
                                e.target.value
                              )
                            }

                            className={`
                              px-3
                              py-2
                              rounded
                              border
                              ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }
                            `}

                          >

                            {
                              branches.map(
                                (b) => (

                                  <option
                                    key={b.id}
                                    value={b.id}
                                  >
                                    {
                                      b.name ||
                                      `Branch ${b.id}`
                                    }
                                  </option>

                                )
                              )
                            }

                          </select>


                          <SecondaryBtn

                            onClick={() =>
                              fetchPendingList(
                                pendingModuleView
                              )
                            }

                            disabled={
                              pendingListLoading
                            }

                          >

                            <FaEye />
                            Search

                          </SecondaryBtn>


                          <SecondaryBtn
                            onClick={handlePrint}
                          >
                            <FaPrint />
                            Print
                          </SecondaryBtn>


                          <DangerBtn
                            onClick={
                              cancelSelected
                            }
                          >
                            <FaTimesCircle />
                            Cancel Selected
                          </DangerBtn>


                          <SecondaryBtn

                            onClick={() => {

                              setPendingModuleView(
                                null
                              );

                              setPendingList(
                                []
                              );

                              setSelectedIds(
                                {}
                              );

                            }}

                          >
                            <FaArrowLeft />
                            Back to Summary
                          </SecondaryBtn>

                        </div>

                      </div>


                      <div
                        className={`
                          rounded-xl
                          border
                          overflow-hidden
                          ${
                            isDark
                              ? "border-gray-700"
                              : "border-gray-200"
                          }
                        `}
                      >

                        <div className="overflow-x-auto">

                          <table className="w-full">

                            <thead
                              className={`
                                ${buttonClass}
                                text-white
                              `}
                            >

                              <tr>

                                <th
                                  className="
                                    px-4
                                    py-3
                                    text-center
                                  "
                                >

                                  Select

                                  <div className="mt-1">

                                    <input

                                      type="checkbox"

                                      onChange={(e) =>
                                        toggleSelectAll(
                                          e.target.checked
                                        )
                                      }

                                      checked={
                                        pendingList.length >
                                          0 &&
                                        Object.keys(
                                          selectedIds
                                        ).length ===
                                          pendingList.length
                                      }

                                    />

                                  </div>

                                </th>


                                <th
                                  className="
                                    px-4
                                    py-3
                                    text-center
                                  "
                                >
                                  ID
                                </th>


                                <th
                                  className="
                                    px-4
                                    py-3
                                    text-center
                                  "
                                >
                                  Details
                                </th>

                              </tr>

                            </thead>


                            <tbody>

                              {
                                pendingListLoading ? (

                                  <tr>

                                    <td
                                      colSpan={3}
                                      className="
                                        px-4
                                        py-6
                                        text-center
                                      "
                                    >
                                      Loading...
                                    </td>

                                  </tr>

                                ) : pendingList.length ===
                                  0 ? (

                                  <tr>

                                    <td
                                      colSpan={3}
                                      className="
                                        px-4
                                        py-6
                                        text-center
                                      "
                                    >
                                      No records found
                                    </td>

                                  </tr>

                                ) : (

                                  pendingList.map(
                                    (
                                      row,
                                      idx
                                    ) => {

                                      const rid =
                                        row.fld_id ??
                                        row.id ??
                                        row.fld_refid;

                                      const ridKey =
                                        String(
                                          rid
                                        );


                                      return (

                                        <tr
                                          key={`${ridKey}-${idx}`}
                                          className={
                                            idx %
                                              2 ===
                                            0
                                              ? isDark
                                                ? "bg-gray-800"
                                                : "bg-gray-50"
                                              : isDark
                                              ? "bg-gray-900"
                                              : "bg-white"
                                          }
                                        >

                                          <td
                                            className="
                                              px-4
                                              py-3
                                              text-center
                                            "
                                          >

                                            <input

                                              type="checkbox"

                                              checked={
                                                !!selectedIds[
                                                  ridKey
                                                ]
                                              }

                                              onChange={(e) =>
                                                toggleRow(
                                                  ridKey,
                                                  e
                                                    .target
                                                    .checked
                                                )
                                              }

                                            />

                                          </td>


                                          <td
                                            className="
                                              px-4
                                              py-3
                                              text-center
                                            "
                                          >
                                            {ridKey}
                                          </td>


                                          <td
                                            className="
                                              px-4
                                              py-3
                                              text-sm
                                            "
                                          >

                                            <div
                                              className="
                                                grid
                                                grid-cols-12
                                                gap-2
                                              "
                                            >

                                              {
                                                Object.entries(
                                                  row
                                                ).map(
                                                  ([
                                                    key,
                                                    value,
                                                  ]) => (

                                                    <div
                                                      key={
                                                        key
                                                      }
                                                      className="
                                                        col-span-12
                                                        md:col-span-6
                                                      "
                                                    >

                                                      <span className="opacity-70">
                                                        {
                                                          key
                                                        }
                                                        :
                                                      </span>

                                                      {" "}

                                                      <span className="font-medium">
                                                        {
                                                          String(
                                                            value
                                                          )
                                                        }
                                                      </span>

                                                    </div>

                                                  )
                                                )
                                              }

                                            </div>

                                          </td>

                                        </tr>

                                      );

                                    }
                                  )

                                )
                              }

                            </tbody>

                          </table>

                        </div>

                      </div>

                    </div>

                  ) : (

                    <div>

                      <div
                        className="
                          flex
                          flex-wrap
                          justify-between
                          items-center
                          gap-3
                          mb-4
                        "
                      >

                        <div>

                          <div
                            className="
                              text-sm
                              opacity-80
                            "
                          >
                            Acc Year
                          </div>

                          <div
                            className="
                              text-lg
                              font-bold
                            "
                          >
                            {finYear}
                          </div>

                        </div>


                        <div
                          className="
                            flex
                            gap-2
                          "
                        >

                          <SecondaryBtn
                            onClick={
                              handlePrint
                            }
                          >
                            <FaPrint />
                            Print
                          </SecondaryBtn>


                          <SecondaryBtn

                            onClick={() =>
                              loadPendingSummary(
                                finYear
                              )
                            }

                          >
                            <FaRedo />
                            Refresh
                          </SecondaryBtn>

                        </div>

                      </div>


                      <div className="overflow-x-auto">

                        <table
                          className="
                            w-full
                            border
                            rounded-xl
                          "
                        >

                          <thead
                            className={`
                              ${buttonClass}
                              text-white
                            `}
                          >

                            <tr>

                              <th className="px-4 py-3 text-left">
                                Module
                              </th>

                              <th className="px-4 py-3 text-center">
                                Count
                              </th>

                              <th className="px-4 py-3 text-center">
                                Status
                              </th>

                              <th className="px-4 py-3 text-center">
                                View
                              </th>

                            </tr>

                          </thead>


                          <tbody>

                            {
                              [
                                "PR",
                                "QTN",
                                "PO",
                                "GRN",
                                "BP",
                                "TALLY",
                              ].map(
                                (
                                  m,
                                  idx
                                ) => {

                                  const cnt =
                                    pendingSummary
                                      ?.counts?.[
                                      m
                                    ] ?? 0;

                                  const status =
                                    pendingSummary
                                      ?.status?.[
                                      m
                                    ] ?? "-";


                                  return (

                                    <tr
                                      key={m}
                                      className={
                                        idx %
                                          2 ===
                                        0
                                          ? isDark
                                            ? "bg-gray-800"
                                            : "bg-gray-50"
                                          : isDark
                                          ? "bg-gray-900"
                                          : "bg-white"
                                      }
                                    >

                                      <td className="px-4 py-3 font-medium">
                                        {
                                          niceModuleName(
                                            m
                                          )
                                        }
                                      </td>


                                      <td className="px-4 py-3 text-center">
                                        {cnt}
                                      </td>


                                      <td className="px-4 py-3 text-center">

                                        {
                                          status ===
                                          "Completed" ? (

                                            <Badge
                                              text="Completed"
                                              kind="ok"
                                            />

                                          ) : status ===
                                            "Not Configured" ? (

                                            <Badge
                                              text="Not Configured"
                                              kind="error"
                                            />

                                          ) : (

                                            <Badge
                                              text="Pending-Open"
                                              kind="warn"
                                            />

                                          )
                                        }

                                      </td>


                                      <td className="px-4 py-3 text-center">

                                        <button

                                          type="button"

                                          className="
                                            underline
                                            inline-flex
                                            items-center
                                            gap-2
                                          "

                                          onClick={() =>
                                            fetchPendingList(
                                              m
                                            )
                                          }

                                        >
                                          <FaEye />
                                          View
                                        </button>

                                      </td>

                                    </tr>

                                  );

                                }
                              )
                            }

                          </tbody>

                        </table>

                      </div>


                      <div className="mt-4">

                        {
                          canGoNextFromPending ? (

                            <div
                              className="
                                text-green-500
                                font-semibold
                              "
                            >
                              ✔ Allowed to proceed
                              (fld_finyearallowchange = 1)
                            </div>

                          ) : (

                            <div
                              className="
                                text-red-500
                                font-semibold
                              "
                            >
                              ✖ Not allowed to proceed.
                            </div>

                          )
                        }

                      </div>


                      <StepNav

                        backDisabled={false}

                        nextDisabled={
                          !canGoNextFromPending
                        }

                        onBack={() =>
                          setPanel(0)
                        }

                        onNext={() =>
                          setPanel(2)
                        }

                      />

                    </div>

                  )
                }

              </Card>

            )
          }


          {/* ================================================= */}
          {/* PANEL 2 - BACKUP / RESTORE */}
          {/* ================================================= */}

          {
            panel === 2 && (

              <Card

                title="Step-2: Backup & Restore (Inventory + Tally)"

                right={
                  <Badge
                    text="Step 2/6"
                    kind="warn"
                  />
                }

              >

                <div className="space-y-4">

                  <div
                    className="
                      text-sm
                      opacity-80
                    "
                  >
                    This will create the new Financial Year databases by backing up and restoring the current databases.
                  </div>


                  {/* INVENTORY */}

                  <div
                    className={`
                      p-4
                      rounded-xl
                      border
                      ${
                        isDark
                          ? "border-gray-700 bg-gray-800"
                          : "border-gray-200 bg-gray-50"
                      }
                    `}
                  >

                    <div
                      className="
                        flex
                        flex-wrap
                        justify-between
                        items-center
                        gap-3
                      "
                    >

                      <div>

                        <div
                          className="
                            font-bold
                            text-lg
                            flex
                            items-center
                            gap-2
                          "
                        >
                          <FaDatabase />
                          Inventory
                        </div>

                        <div className="text-xs opacity-70">
                          Current: {info.curInvDb}
                        </div>

                        <div className="text-xs opacity-70">
                          New: {info.newInvDb}
                        </div>


                        {
                          backupStatus
                            .Inventory
                            .done && (

                            <div
                              className="
                                mt-2
                                text-green-500
                                font-semibold
                              "
                            >
                              ✓ Backup & Restore Successful
                            </div>

                          )
                        }

                      </div>


                      <PrimaryBtn

                        onClick={() =>
                          doBackupRestore(
                            "Inventory"
                          )
                        }

                        disabled={
                          backupStatus
                            .Inventory
                            .done
                        }

                      >
                        <FaPlay />
                        Backup & Restore
                      </PrimaryBtn>

                    </div>

                  </div>


                  {/* TALLY */}

                  <div
                    className={`
                      p-4
                      rounded-xl
                      border
                      ${
                        isDark
                          ? "border-gray-700 bg-gray-800"
                          : "border-gray-200 bg-gray-50"
                      }
                    `}
                  >

                    <div
                      className="
                        flex
                        flex-wrap
                        justify-between
                        items-center
                        gap-3
                      "
                    >

                      <div>

                        <div
                          className="
                            font-bold
                            text-lg
                            flex
                            items-center
                            gap-2
                          "
                        >
                          <FaDatabase />
                          Tally
                        </div>

                        <div className="text-xs opacity-70">
                          Current: {info.curTallyDb}
                        </div>

                        <div className="text-xs opacity-70">
                          New: {info.newTallyDb}
                        </div>


                        {
                          backupStatus
                            .Tally
                            .done && (

                            <div
                              className="
                                mt-2
                                text-green-500
                                font-semibold
                              "
                            >
                              ✓ Backup & Restore Successful
                            </div>

                          )
                        }

                      </div>


                      <PrimaryBtn

                        onClick={() =>
                          doBackupRestore(
                            "Tally"
                          )
                        }

                        disabled={
                          !backupStatus
                            .Inventory
                            .done ||
                          backupStatus
                            .Tally
                            .done
                        }

                      >
                        <FaPlay />
                        Backup & Restore
                      </PrimaryBtn>

                    </div>

                  </div>


                  <StepNav

                    backDisabled={false}

                    nextDisabled={
                      !step2Ready
                    }

                    onBack={() =>
                      setPanel(1)
                    }

                    onNext={() =>
                      setPanel(3)
                    }

                  />

                </div>

              </Card>

            )
          }


          {/* ================================================= */}
          {/* PANEL 3 - CHECK / COMPARE */}
          {/* ================================================= */}

          {
            panel === 3 && (

              <Card

                title="Step-3: Check / Compare Data (Inventory + Tally)"

                right={
                  <Badge
                    text="Step 3/6"
                    kind="warn"
                  />
                }

              >

                <div className="space-y-5">

                  <div className="text-sm opacity-80">

                    Compare important table counts between the current database and the newly restored Financial Year database.

                  </div>


                  {/* INVENTORY CHECK */}

                  <div
                    className={`
                      p-4
                      rounded-xl
                      border
                      ${
                        isDark
                          ? "border-gray-700 bg-gray-800"
                          : "border-gray-200 bg-gray-50"
                      }
                    `}
                  >

                    <div
                      className="
                        flex
                        justify-between
                        items-center
                        flex-wrap
                        gap-3
                      "
                    >

                      <div>

                        <div
                          className="
                            font-bold
                            text-lg
                            flex
                            items-center
                            gap-2
                          "
                        >
                          <FaDatabase />
                          Inventory Database
                        </div>

                        <div className="text-xs opacity-70">
                          Old: {info.curInvDb}
                        </div>

                        <div className="text-xs opacity-70">
                          New: {info.newInvDb}
                        </div>

                      </div>


                      <PrimaryBtn

                        onClick={() =>
                          doCheck(
                            "Inventory"
                          )
                        }

                        disabled={
                          checkStatus
                            .Inventory
                            .done
                        }

                      >
                        <FaPlay />
                        Check / Compare
                      </PrimaryBtn>

                    </div>


                    {
                      checkStatus
                        .Inventory
                        .results
                        .length > 0 && (

                        <div
                          className="
                            mt-4
                            overflow-x-auto
                          "
                        >

                          <table
                            className="
                              w-full
                              border
                            "
                          >

                            <thead
                              className={`
                                ${buttonClass}
                                text-white
                              `}
                            >

                              <tr>

                                <th className="px-4 py-3 text-left">
                                  Check
                                </th>

                                <th className="px-4 py-3 text-center">
                                  Old DB
                                </th>

                                <th className="px-4 py-3 text-center">
                                  New DB
                                </th>

                                <th className="px-4 py-3 text-center">
                                  Status
                                </th>

                              </tr>

                            </thead>


                            <tbody>

                              {
                                checkStatus
                                  .Inventory
                                  .results
                                  .map(
                                    (
                                      row,
                                      idx
                                    ) => (

                                      <tr
                                        key={idx}
                                        className={
                                          idx %
                                            2 ===
                                          0
                                            ? isDark
                                              ? "bg-gray-900"
                                              : "bg-gray-50"
                                            : isDark
                                            ? "bg-gray-800"
                                            : "bg-white"
                                        }
                                      >

                                        <td className="px-4 py-3 font-medium">
                                          {
                                            row.check ??
                                            "-"
                                          }
                                        </td>

                                        <td className="px-4 py-3 text-center">
                                          {
                                            row.oldCount ??
                                            "-"
                                          }
                                        </td>

                                        <td className="px-4 py-3 text-center">
                                          {
                                            row.newCount ??
                                            "-"
                                          }
                                        </td>

                                        <td className="px-4 py-3 text-center">

                                          <Badge

                                            text={
                                              row.status ||
                                              "-"
                                            }

                                            kind={
                                              row.status ===
                                              "Success"
                                                ? "ok"
                                                : "error"
                                            }

                                          />

                                        </td>

                                      </tr>

                                    )
                                  )
                              }

                            </tbody>

                          </table>

                        </div>

                      )
                    }

                  </div>


                  {/* TALLY CHECK */}

                  <div
                    className={`
                      p-4
                      rounded-xl
                      border
                      ${
                        isDark
                          ? "border-gray-700 bg-gray-800"
                          : "border-gray-200 bg-gray-50"
                      }
                    `}
                  >

                    <div
                      className="
                        flex
                        justify-between
                        items-center
                        flex-wrap
                        gap-3
                      "
                    >

                      <div>

                        <div
                          className="
                            font-bold
                            text-lg
                            flex
                            items-center
                            gap-2
                          "
                        >
                          <FaDatabase />
                          Tally Database
                        </div>

                        <div className="text-xs opacity-70">
                          Old: {info.curTallyDb}
                        </div>

                        <div className="text-xs opacity-70">
                          New: {info.newTallyDb}
                        </div>

                      </div>


                      <PrimaryBtn

                        onClick={() =>
                          doCheck(
                            "Tally"
                          )
                        }

                        disabled={
                          !checkStatus
                            .Inventory
                            .done ||
                          checkStatus
                            .Tally
                            .done
                        }

                      >
                        <FaPlay />
                        Check / Compare
                      </PrimaryBtn>

                    </div>


                    {
                      checkStatus
                        .Tally
                        .results
                        .length > 0 && (

                        <div
                          className="
                            mt-4
                            overflow-x-auto
                          "
                        >

                          <table className="w-full border">

                            <thead
                              className={`
                                ${buttonClass}
                                text-white
                              `}
                            >

                              <tr>

                                <th className="px-4 py-3 text-left">
                                  Check
                                </th>

                                <th className="px-4 py-3 text-center">
                                  Old DB
                                </th>

                                <th className="px-4 py-3 text-center">
                                  New DB
                                </th>

                                <th className="px-4 py-3 text-center">
                                  Status
                                </th>

                              </tr>

                            </thead>


                            <tbody>

                              {
                                checkStatus
                                  .Tally
                                  .results
                                  .map(
                                    (
                                      row,
                                      idx
                                    ) => (

                                      <tr
                                        key={idx}
                                      >

                                        <td className="px-4 py-3 font-medium">
                                          {
                                            row.check ??
                                            "-"
                                          }
                                        </td>

                                        <td className="px-4 py-3 text-center">
                                          {
                                            row.oldCount ??
                                            "-"
                                          }
                                        </td>

                                        <td className="px-4 py-3 text-center">
                                          {
                                            row.newCount ??
                                            "-"
                                          }
                                        </td>

                                        <td className="px-4 py-3 text-center">

                                          <Badge

                                            text={
                                              row.status ||
                                              "-"
                                            }

                                            kind={
                                              row.status ===
                                              "Success"
                                                ? "ok"
                                                : "error"
                                            }

                                          />

                                        </td>

                                      </tr>

                                    )
                                  )
                              }

                            </tbody>

                          </table>

                        </div>

                      )
                    }

                  </div>


                  {
                    step3Ready && (

                      <div
                        className={`
                          p-4
                          rounded-xl
                          border
                          ${
                            isDark
                              ? "bg-green-900/20 border-green-700"
                              : "bg-green-50 border-green-200"
                          }
                        `}
                      >

                        <div
                          className="
                            text-green-600
                            font-semibold
                            flex
                            items-center
                            gap-2
                          "
                        >
                          <FaCheck />
                          Inventory and Tally comparison completed.
                        </div>

                      </div>

                    )
                  }


                  <StepNav

                    backDisabled={false}

                    nextDisabled={
                      !step3Ready
                    }

                    onBack={() =>
                      setPanel(2)
                    }

                    onNext={() =>
                      setPanel(4)
                    }

                  />

                </div>

              </Card>

            )
          }


          {/* ================================================= */}
          {/* PANEL 4 - CLEAR */}
          {/* ================================================= */}

          {
            panel === 4 && (

              <Card

                title="Step-4: Clear New Financial Year Databases"

                right={
                  <Badge
                    text="Step 4/6"
                    kind="warn"
                  />
                }

              >

                <div className="space-y-5">

                  <div
                    className={`
                      p-4
                      rounded-xl
                      border
                      ${
                        isDark
                          ? "bg-red-900/20 border-red-700"
                          : "bg-red-50 border-red-200"
                      }
                    `}
                  >

                    <div
                      className="
                        font-semibold
                        flex
                        items-center
                        gap-2
                        text-red-600
                      "
                    >
                      <FaExclamationTriangle />
                      Warning
                    </div>

                    <div className="mt-1 text-sm">

                      This step deletes transactional data from the NEW Financial Year databases.

                      The old/current FY databases are not cleared.

                    </div>

                  </div>


                  {/* INVENTORY CLEAR */}

                  <div
                    className={`
                      p-4
                      rounded-xl
                      border
                      ${
                        isDark
                          ? "border-gray-700 bg-gray-800"
                          : "border-gray-200 bg-gray-50"
                      }
                    `}
                  >

                    <div
                      className="
                        flex
                        justify-between
                        items-center
                        flex-wrap
                        gap-3
                      "
                    >

                      <div>

                        <div
                          className="
                            text-lg
                            font-bold
                            flex
                            gap-2
                            items-center
                          "
                        >
                          <FaDatabase />
                          Inventory
                        </div>

                        <div className="text-xs opacity-70">
                          New DB: {info.newInvDb}
                        </div>


                        {
                          clearStatus
                            .Inventory
                            .done && (

                            <div
                              className="
                                mt-2
                                text-green-500
                                font-semibold
                              "
                            >
                              ✓ Cleared{" "}
                              {
                                clearStatus
                                  .Inventory
                                  .cleared
                              }{" "}
                              table(s)
                            </div>

                          )
                        }

                      </div>


                      <div className="flex gap-2">

                        <DangerBtn

                          onClick={() =>
                            doClear(
                              "Inventory"
                            )
                          }

                          disabled={
                            clearStatus
                              .Inventory
                              .done
                          }

                        >
                          Clear Inventory
                        </DangerBtn>


                        {
                          clearStatus
                            .Inventory
                            .done && (

                            <SecondaryBtn
                              onClick={() =>
                                resetClear(
                                  "Inventory"
                                )
                              }
                            >
                              <FaRedo />
                              Reset
                            </SecondaryBtn>

                          )
                        }

                      </div>

                    </div>


                    {
                      clearStatus
                        .Inventory
                        .tables
                        ?.length >
                        0 && (

                        <div className="mt-4">

                          <div
                            className="
                              text-sm
                              font-semibold
                              mb-2
                            "
                          >
                            Cleared tables
                          </div>

                          <div
                            className="
                              flex
                              flex-wrap
                              gap-2
                            "
                          >

                            {
                              clearStatus
                                .Inventory
                                .tables
                                .map(
                                  (
                                    table
                                  ) => (

                                    <Badge
                                      key={
                                        table
                                      }
                                      text={
                                        table
                                      }
                                      kind="ok"
                                    />

                                  )
                                )
                            }

                          </div>

                        </div>

                      )
                    }

                  </div>


                  {/* TALLY CLEAR */}

                  <div
                    className={`
                      p-4
                      rounded-xl
                      border
                      ${
                        isDark
                          ? "border-gray-700 bg-gray-800"
                          : "border-gray-200 bg-gray-50"
                      }
                    `}
                  >

                    <div
                      className="
                        flex
                        justify-between
                        items-center
                        flex-wrap
                        gap-3
                      "
                    >

                      <div>

                        <div
                          className="
                            text-lg
                            font-bold
                            flex
                            gap-2
                            items-center
                          "
                        >
                          <FaDatabase />
                          Tally
                        </div>

                        <div className="text-xs opacity-70">
                          New DB: {info.newTallyDb}
                        </div>


                        {
                          clearStatus
                            .Tally
                            .done && (

                            <div
                              className="
                                mt-2
                                text-green-500
                                font-semibold
                              "
                            >
                              ✓ Cleared{" "}
                              {
                                clearStatus
                                  .Tally
                                  .cleared
                              }{" "}
                              table(s)
                            </div>

                          )
                        }

                      </div>


                      <div className="flex gap-2">

                        <DangerBtn

                          onClick={() =>
                            doClear(
                              "Tally"
                            )
                          }

                          disabled={
                            !clearStatus
                              .Inventory
                              .done ||
                            clearStatus
                              .Tally
                              .done
                          }

                        >
                          Clear Tally
                        </DangerBtn>


                        {
                          clearStatus
                            .Tally
                            .done && (

                            <SecondaryBtn
                              onClick={() =>
                                resetClear(
                                  "Tally"
                                )
                              }
                            >
                              <FaRedo />
                              Reset
                            </SecondaryBtn>

                          )
                        }

                      </div>

                    </div>

                  </div>


                  <StepNav

                    backDisabled={false}

                    nextDisabled={
                      !step4Ready
                    }

                    onBack={() =>
                      setPanel(3)
                    }

                    onNext={() =>
                      setPanel(5)
                    }

                  />

                </div>

              </Card>

            )
          }


          {/* ================================================= */}
          {/* PANEL 5 - OPENING STOCK */}
          {/* ================================================= */}

          {
            panel === 5 && (

              <Card

                title="Step-5: Add Opening Stock"

                right={
                  <Badge
                    text="Step 5/6"
                    kind="warn"
                  />
                }

              >

                <div className="space-y-5">

                  <div className="text-sm opacity-80">

                    The closing stock from the current Inventory database will be added as opening stock in the new Financial Year database.

                  </div>


                  <div
                    className={`
                      p-5
                      rounded-xl
                      border
                      ${
                        isDark
                          ? "bg-gray-800 border-gray-700"
                          : "bg-gray-50 border-gray-200"
                      }
                    `}
                  >

                    <div
                      className="
                        flex
                        justify-between
                        items-center
                        flex-wrap
                        gap-4
                      "
                    >

                      <div>

                        <div
                          className="
                            text-lg
                            font-bold
                          "
                        >
                          Inventory Opening Stock
                        </div>

                        <div className="text-xs opacity-70">
                          Source: {info.curInvDb}
                        </div>

                        <div className="text-xs opacity-70">
                          Destination: {info.newInvDb}
                        </div>

                      </div>


                      <div className="flex gap-2">

                        <PrimaryBtn

                          onClick={
                            doOpening
                          }

                          disabled={
                            openingStatus
                              .done
                          }

                        >
                          <FaPlay />
                          Add Opening Stock
                        </PrimaryBtn>


                        {
                          openingStatus
                            .done && (

                            <SecondaryBtn
                              onClick={
                                resetOpening
                              }
                            >
                              <FaRedo />
                              Reset
                            </SecondaryBtn>

                          )
                        }

                      </div>

                    </div>

                  </div>


                  {
                    openingStatus
                      .status && (

                      <div
                        className="
                          overflow-x-auto
                        "
                      >

                        <table className="w-full border">

                          <thead
                            className={`
                              ${buttonClass}
                              text-white
                            `}
                          >

                            <tr>

                              <th className="px-4 py-3 text-left">
                                Check
                              </th>

                              <th className="px-4 py-3 text-center">
                                Status
                              </th>

                            </tr>

                          </thead>


                          <tbody>

                            {
                              Object.entries(
                                openingStatus
                                  .status
                              ).map(
                                ([
                                  key,
                                  value,
                                ]) => (

                                  <tr
                                    key={
                                      key
                                    }
                                  >

                                    <td className="px-4 py-3">
                                      {key}
                                    </td>

                                    <td className="px-4 py-3 text-center">

                                      <Badge

                                        text={
                                          String(
                                            value
                                          )
                                        }

                                        kind={
                                          value ===
                                          "Success"
                                            ? "ok"
                                            : "error"
                                        }

                                      />

                                    </td>

                                  </tr>

                                )
                              )
                            }

                          </tbody>

                        </table>

                      </div>

                    )
                  }


                  {
                    openingStatus
                      .counts && (

                      <div
                        className={`
                          p-4
                          rounded-xl
                          border
                          ${
                            isDark
                              ? "bg-gray-800 border-gray-700"
                              : "bg-gray-50 border-gray-200"
                          }
                        `}
                      >

                        <div
                          className="
                            font-semibold
                            mb-3
                          "
                        >
                          Opening Stock Counts
                        </div>


                        <div
                          className="
                            grid
                            grid-cols-12
                            gap-3
                          "
                        >

                          {
                            Object.entries(
                              openingStatus
                                .counts
                            ).map(
                              ([
                                key,
                                value,
                              ]) => (

                                <div
                                  key={
                                    key
                                  }
                                  className="
                                    col-span-12
                                    md:col-span-6
                                    p-3
                                    rounded-lg
                                    border
                                  "
                                >

                                  <div
                                    className="
                                      text-xs
                                      opacity-70
                                    "
                                  >
                                    {key}
                                  </div>

                                  <div
                                    className="
                                      text-lg
                                      font-bold
                                    "
                                  >
                                    {
                                      String(
                                        value
                                      )
                                    }
                                  </div>

                                </div>

                              )
                            )
                          }

                        </div>

                      </div>

                    )
                  }


                  {
                    step5Ready && (

                      <div
                        className={`
                          p-4
                          rounded-xl
                          border
                          ${
                            isDark
                              ? "bg-green-900/20 border-green-700"
                              : "bg-green-50 border-green-200"
                          }
                        `}
                      >

                        <div
                          className="
                            text-green-600
                            font-semibold
                            flex
                            items-center
                            gap-2
                          "
                        >
                          <FaCheck />
                          Opening stock process completed.
                        </div>

                      </div>

                    )
                  }


                  <StepNav

                    backDisabled={false}

                    nextDisabled={
                      !step5Ready
                    }

                    onBack={() =>
                      setPanel(4)
                    }

                    onNext={() =>
                      setPanel(6)
                    }

                  />

                </div>

              </Card>

            )
          }


          {/* ================================================= */}
          {/* PANEL 6 - FINALIZE */}
          {/* ================================================= */}

          {
            panel === 6 && (

              <Card

                title="Step-6: Finalize Financial Year Change"

                right={
                  <Badge
                    text="Step 6/6"
                    kind={
                      finalStatus.done
                        ? "ok"
                        : "warn"
                    }
                  />
                }

              >

                <div className="space-y-5">


                  {
                    !finalStatus
                      .done && (

                      <>

                        <div
                          className={`
                            p-4
                            rounded-xl
                            border
                            ${
                              isDark
                                ? "bg-red-900/20 border-red-700"
                                : "bg-red-50 border-red-200"
                            }
                          `}
                        >

                          <div
                            className="
                              text-red-600
                              font-bold
                              flex
                              items-center
                              gap-2
                            "
                          >
                            <FaExclamationTriangle />
                            Final Confirmation
                          </div>

                          <div className="mt-2 text-sm">

                            This step switches the application's live Inventory and Tally database connections from:

                          </div>


                          <div
                            className="
                              mt-3
                              grid
                              grid-cols-12
                              gap-3
                            "
                          >

                            <div
                              className="
                                col-span-12
                                md:col-span-6
                                p-3
                                rounded-lg
                                border
                              "
                            >

                              <div className="font-semibold">
                                Current Financial Year
                              </div>

                              <div className="text-lg font-bold">
                                {info.activeFY}
                              </div>

                              <div className="text-xs opacity-70">
                                {info.curInvDb}
                              </div>

                              <div className="text-xs opacity-70">
                                {info.curTallyDb}
                              </div>

                            </div>


                            <div
                              className="
                                col-span-12
                                md:col-span-6
                                p-3
                                rounded-lg
                                border
                              "
                            >

                              <div className="font-semibold">
                                New Financial Year
                              </div>

                              <div className="text-lg font-bold">
                                {info.nextFY}
                              </div>

                              <div className="text-xs opacity-70">
                                {info.newInvDb}
                              </div>

                              <div className="text-xs opacity-70">
                                {info.newTallyDb}
                              </div>

                            </div>

                          </div>


                          <div className="mt-4 font-semibold">

                            Once finalized, users should log out and login again so new database connections are loaded.

                          </div>

                        </div>


                        <div
                          className="
                            flex
                            justify-between
                            items-center
                          "
                        >

                          <SecondaryBtn
                            onClick={() =>
                              setPanel(5)
                            }
                          >
                            <FaArrowLeft />
                            Back
                          </SecondaryBtn>


                          <DangerBtn
                            onClick={
                              doFinalize
                            }
                          >
                            <FaCheck />
                            Finalize Financial Year
                          </DangerBtn>

                        </div>

                      </>

                    )
                  }


                  {
                    finalStatus
                      .done && (

                      <div
                        className={`
                          p-6
                          rounded-xl
                          border
                          ${
                            isDark
                              ? "bg-green-900/20 border-green-700"
                              : "bg-green-50 border-green-200"
                          }
                        `}
                      >

                        <div
                          className="
                            text-green-600
                            font-bold
                            text-xl
                            flex
                            items-center
                            gap-2
                          "
                        >
                          <FaCheck />
                          Financial Year Changed Successfully
                        </div>


                        <div
                          className="
                            mt-5
                            grid
                            grid-cols-12
                            gap-4
                          "
                        >

                          <div
                            className="
                              col-span-12
                              md:col-span-6
                            "
                          >

                            <div className="text-sm opacity-70">
                              Previous Financial Year
                            </div>

                            <div className="text-lg font-bold">
                              {
                                finalStatus
                                  .fromFinYear
                              }
                            </div>

                          </div>


                          <div
                            className="
                              col-span-12
                              md:col-span-6
                            "
                          >

                            <div className="text-sm opacity-70">
                              New Financial Year
                            </div>

                            <div className="text-lg font-bold">
                              {
                                finalStatus
                                  .toFinYear
                              }
                            </div>

                          </div>


                          <div
                            className="
                              col-span-12
                              md:col-span-6
                            "
                          >

                            <div className="text-sm opacity-70">
                              Active Inventory DB
                            </div>

                            <div className="font-semibold">
                              {
                                finalStatus
                                  .newInvDb
                              }
                            </div>

                          </div>


                          <div
                            className="
                              col-span-12
                              md:col-span-6
                            "
                          >

                            <div className="text-sm opacity-70">
                              Active Tally DB
                            </div>

                            <div className="font-semibold">
                              {
                                finalStatus
                                  .newTallyDb
                              }
                            </div>

                          </div>

                        </div>


                        <div
                          className="
                            mt-5
                            text-sm
                          "
                        >

                          Please logout and login again before continuing normal application operations.

                        </div>

                      </div>

                    )
                  }

                </div>

              </Card>

            )
          }


          {/* ================================================= */}
          {/* GLOBAL LOADING */}
          {/* ================================================= */}

          {
            loading && (

              <div
                className="
                  fixed
                  inset-0
                  z-50
                  bg-black/30
                  flex
                  items-center
                  justify-center
                "
              >

                <div
                  className={`
                    px-6
                    py-4
                    rounded-xl
                    shadow-xl
                    font-semibold
                    ${
                      isDark
                        ? "bg-gray-800 text-white"
                        : "bg-white text-gray-900"
                    }
                  `}
                >
                  Processing... Please wait.
                </div>

              </div>

            )
          }


          <ToastContainer

            position="top-right"

            autoClose={3000}

            hideProgressBar={false}

            closeOnClick

            pauseOnHover

            theme={
              isDark
                ? "dark"
                : "light"
            }

          />


        </div>

      </div>

    </AdminLayoutWrapper>

  );

};


export default FinYearDataBaseChange;